import type { EventType } from '@cocoonspace/sdk-js/v2/types/eventType'
import type { DraggableAttributes } from '@dnd-kit/core'
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import { useMemo } from 'react'
import { cn } from '@cocoonspace/ui/lib/utils'
import { GripVerticalIcon, MapPinIcon } from 'lucide-react'

import { DurationBadge } from '~/components/duration'
import {
	ScheduleCard,
	ScheduleCardActions,
	ScheduleCardDescription,
	ScheduleCardHeader,
	ScheduleCardTitle,
} from '../../scheduling-card'
import { useListVenuesGroupedById } from '../../use-list-venues-grouped-by-id'
import { OnsiteBadge, RemoteBadge } from '../event-type-badges'
import { CopyLinkAction } from './copy-link-action'
import { CopyEmbedAction } from './copy-embed-action'
import { DeleteAction } from './delete-action'
import { EditAction } from './edit-action'
import { ToggleEnabledAction } from './toggle-enabled-action'

const EventTypeAddress = ({ eventType }: { eventType: EventType }) => {
	const { data: venues } = useListVenuesGroupedById()

	const address = useMemo(() => {
		if (eventType.location?.type === 'cocoon' && eventType.location.venue_id) {
			const { address1, postal_code, city } =
				venues?.[eventType.location.venue_id]?.address ?? {}

			return `${address1 || ''}, ${postal_code || ''} ${city || ''}`
		}

		return undefined
	}, [eventType, venues])

	return (
		<div className='flex flex-row items-center gap-1 text-sm'>
			<MapPinIcon />
			<span className='truncate'>{address}</span>
		</div>
	)
}

const BadgesList = ({ eventType }: { eventType: EventType }) => (
	<div className='flex items-end gap-2'>
		<DurationBadge
			className='shrink-0 gap-1 [&_svg]:!size-3'
			duration={eventType.duration}
			size='sm'
		/>

		{eventType.location?.type === 'remote' ? <RemoteBadge /> : <OnsiteBadge />}
	</div>
)

export const EventTypeCardHandle = ({
	dragHandleProps,
}: {
	dragHandleProps?: {
		attributes?: DraggableAttributes
		listeners?: SyntheticListenerMap | undefined
	}
}) => (
	<div
		className={cn('text-muted-foreground cursor-grab touch-none select-none', {
			'hover:text-foreground': dragHandleProps,
		})}
		{...(dragHandleProps?.listeners ?? {})}
		{...(dragHandleProps?.attributes ?? {})}
	>
		<GripVerticalIcon />
	</div>
)

export const EventTypeCardEdition = ({
	eventType,
	isPublic = false,
}: {
	eventType: EventType
	isPublic?: boolean
}) => (
	<div className='order-first flex items-center justify-end gap-2 md:order-last'>
		<ToggleEnabledAction
			eventType={eventType}
			isPublic={isPublic}
		/>

		<ScheduleCardActions>
			<CopyLinkAction eventType={eventType} />
			<CopyEmbedAction eventType={eventType} />
			<EditAction eventType={eventType} />
			<DeleteAction eventType={eventType} />
		</ScheduleCardActions>
	</div>
)

export const EventTypeCard = ({
	eventType,
	className,
	prepend = () => null,
	append = () => null,
	...props
}: {
	eventType: EventType
	className?: string
	dragHandleProps?: {
		attributes?: DraggableAttributes
		listeners?: SyntheticListenerMap | undefined
	}
	prepend?: (eventType: EventType) => React.ReactNode
	append?: (eventType: EventType) => React.ReactNode
} & React.ComponentProps<typeof ScheduleCard>) => {
	const showAddress =
		eventType.location?.type === 'cocoon' && eventType.location.venue_id

	return (
		<ScheduleCard
			className={cn('flex-row items-center gap-4', className, {
				'opacity-50': !eventType.enabled,
			})}
			{...props}
		>
			{prepend?.(eventType)}

			<div className='flex flex-1 flex-col gap-4 md:flex-row md:items-center'>
				<div className='flex flex-1 flex-col gap-2 leading-none'>
					<ScheduleCardHeader>
						<ScheduleCardTitle>{eventType.title}</ScheduleCardTitle>
					</ScheduleCardHeader>

					{eventType.description && (
						<ScheduleCardDescription>
							{eventType.description}
						</ScheduleCardDescription>
					)}

					{showAddress && <EventTypeAddress eventType={eventType} />}
					<BadgesList eventType={eventType} />
				</div>

				{append?.(eventType)}
			</div>
		</ScheduleCard>
	)
}
